import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  Stack,
  Chip,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Icons
import LocationOnIcon      from '@mui/icons-material/LocationOn';
import LocalParkingIcon    from '@mui/icons-material/LocalParking';
import RestaurantIcon      from '@mui/icons-material/Restaurant';
import KitchenIcon         from '@mui/icons-material/Kitchen';
import DirectionsIcon      from '@mui/icons-material/Directions';
import AccessTimeIcon      from '@mui/icons-material/AccessTime';
import PhoneIcon           from '@mui/icons-material/Phone';
import EmailIcon           from '@mui/icons-material/Email';
import WifiIcon            from '@mui/icons-material/Wifi';
import AccessibilityIcon   from '@mui/icons-material/Accessibility';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import DirectionsBusIcon   from '@mui/icons-material/DirectionsBus';
import DirectionsCarIcon   from '@mui/icons-material/DirectionsCar';
import PedalBikeIcon       from '@mui/icons-material/PedalBike';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import OpenInNewIcon       from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Images
import batisse1 from '../assets/batisse/batisse1.jpeg';
import batisse2 from '../assets/batisse/batisse2.jpeg';
import batisse3 from '../assets/batisse/batisse3.jpeg';
import batisse4 from '../assets/batisse/batisse4.jpeg';
import batisse5 from '../assets/batisse/batisse5.jpeg';

// ─── Coordonnées ──────────────────────────────────────────────────────────────
const LAT = 45.76566316445998;
const LNG = -73.99688135816288;
const OSM_URL    = `https://www.openstreetmap.org/?mlat=${LAT}&mlon=${LNG}#map=17/${LAT}/${LNG}`;
const OSM_EMBED  = `https://www.openstreetmap.org/export/embed.html?bbox=${LNG-0.005}%2C${LAT-0.005}%2C${LNG+0.005}%2C${LAT+0.005}&layer=mapnik&marker=${LAT}%2C${LNG}`;

// ─── Animations ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const bounceY = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(7px); }
`;

// ─── Données ───────────────────────────────────────────────────────────────────
const TRANSPORTS = [
  {
    icon: <DirectionsCarIcon  sx={{ fontSize: 36 }} />,
    title: 'En voiture',
    sub:   'Stationnement gratuit sur place',
    desc:  'Facile d\'accès depuis l\'autoroute 15. Plusieurs places de stationnement directement devant le bâtiment.',
    color: '#1565c0',
  },
  {
    icon: <DirectionsBusIcon  sx={{ fontSize: 36 }} />,
    title: 'Transport en commun',
    sub:   'Arrêts de bus à proximité',
    desc:  'Lignes 4 et 9 à moins de 5 minutes à pied depuis notre porte.',
    color: '#2e7d32',
  },
  {
    icon: <PedalBikeIcon      sx={{ fontSize: 36 }} />,
    title: 'À vélo',
    sub:   'Piste cyclable accessible',
    desc:  'Support à vélos disponible sur place — venez pédaler jusqu\'à votre formation !',
    color: '#e65100',
  },
];

const SERVICES = [
  { icon: <LocalParkingIcon  sx={{ fontSize: 28, color: '#c62828' }} />, title: 'Stationnement',   desc: 'Gratuit et disponible devant le bâtiment' },
  { icon: <KitchenIcon       sx={{ fontSize: 28, color: '#c62828' }} />, title: 'Cuisine',          desc: 'Micro-ondes, réfrigérateur, espace repas' },
  { icon: <RestaurantIcon    sx={{ fontSize: 28, color: '#c62828' }} />, title: 'Restauration',     desc: 'Plusieurs options à moins de 5 min à pied' },
  { icon: <WifiIcon          sx={{ fontSize: 28, color: '#c62828' }} />, title: 'Wi-Fi gratuit',    desc: 'Connexion haut débit incluse' },
  { icon: <AccessibilityIcon sx={{ fontSize: 28, color: '#c62828' }} />, title: 'Accessibilité',    desc: 'Bâtiment entièrement accessible PMR' },
];

const RESTAURANTS = [
  { name: 'Café Bistro Le Saint-Jérôme', distance: '3 min' },
  { name: 'Restaurant La Piazza',         distance: '5 min' },
  { name: 'Subway',                        distance: '2 min' },
  { name: 'Tim Hortons',                   distance: '4 min' },
  { name: 'Dépanneur 7Jours',             distance: '1 min' },
];

// ─── Styled ────────────────────────────────────────────────────────────────────
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: `linear-gradient(155deg, rgba(180,20,20,0.97) 0%, rgba(90,0,0,0.93) 100%), url(${batisse1})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: '#fff',
  padding: theme.spacing(17, 0, 14),
  textAlign: 'center',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -1, left: 0, right: 0,
    height: '70px',
    background: '#ffffff',
    clipPath: 'ellipse(60% 100% at 50% 100%)',
  },
  // Cercles décoratifs
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-90px', right: '-90px',
    width: '380px', height: '380px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
    pointerEvents: 'none',
  },
}));

const Section = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'alt',
})(({ theme, alt }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: alt ? '#f7f8fc' : '#ffffff',
  width: '100%',
}));

const Wrap = styled(Box)(({ theme }) => ({
  maxWidth: '1160px',
  margin: '0 auto',
  padding: theme.spacing(0, 3),
}));

const SectionLabel = styled(Typography)(() => ({
  fontSize: '0.78rem',
  fontWeight: 700,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#c62828',
  marginBottom: '10px',
  display: 'block',
  textAlign: 'center',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  paddingBottom: theme.spacing(2.5),
  marginBottom: theme.spacing(5),
  textAlign: 'center',
  fontWeight: 800,
  color: '#1a1a1a',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0, left: '50%',
    transform: 'translateX(-50%)',
    width: '44px', height: '4px',
    background: 'linear-gradient(90deg, #c62828, #ff6b6b)',
    borderRadius: '2px',
  },
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  border: '1px solid rgba(198,40,40,0.12)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.07)',
  overflow: 'hidden',
  height: '100%',
}));

const TransportCard = styled(Paper)(({ theme, cardcolor }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  border: '1px solid rgba(0,0,0,0.06)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: '4px',
    background: cardcolor || '#c62828',
  },
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 16px 40px rgba(0,0,0,0.1)',
  },
}));

const ServiceRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(0,0,0,0.06)',
  backgroundColor: '#fff',
  transition: 'all 0.25s ease',
  '&:hover': {
    borderColor: 'rgba(198,40,40,0.2)',
    backgroundColor: 'rgba(198,40,40,0.02)',
    transform: 'translateX(4px)',
  },
}));

const CarouselContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  borderRadius: '20px',
  boxShadow: '0 16px 48px rgba(0,0,0,0.14)',
}));

const CarouselBtn = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255,255,255,0.93)',
  color: '#c62828',
  width: 40, height: 40,
  boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
  zIndex: 5,
  '&:hover': { backgroundColor: '#c62828', color: '#fff' },
  '&.left':  { left:  theme.spacing(1.5) },
  '&.right': { right: theme.spacing(1.5) },
}));

const RoundIcon = ({ children, bg = 'rgba(198,40,40,0.1)', color = '#c62828', size = 52 }) => (
  <Box sx={{
    width: size, height: size,
    borderRadius: `${size * 0.28}px`,
    backgroundColor: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color, flexShrink: 0,
  }}>
    {children}
  </Box>
);

// ─── Composant principal ───────────────────────────────────────────────────────
const Emplacement = () => {
  const images = [batisse1, batisse2, batisse3, batisse4, batisse5];
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setImgIdx(p => (p + 1) % images.length), 5000);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <>
      {/* ══════════════════ HERO ══════════════════ */}
      <HeroSection>
        {/* Cercle bas-gauche déco */}
        <Box sx={{ position: 'absolute', bottom: '80px', left: '-60px', width: 240, height: 240, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

        <Wrap sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ animation: `${fadeUp} 0.8s ease both` }}>
            {/* Badge adresse */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              px: 2.5, py: 0.8,
              borderRadius: '50px',
              border: '1px solid rgba(255,255,255,0.35)',
              background: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(4px)',
              mb: 3,
            }}>
              <LocationOnIcon sx={{ fontSize: '1rem' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
                275 Des Laurentides, Saint-Jérôme QC
              </Typography>
            </Box>

            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.8rem' }, fontWeight: 900, mb: 2.5, lineHeight: 1.1 }}>
              Notre emplacement
            </Typography>
            <Typography sx={{ maxWidth: '600px', mx: 'auto', fontSize: '1.1rem', lineHeight: 1.9, opacity: 0.88, mb: 5 }}>
              Retrouvez-nous au cœur de Saint-Jérôme pour vos formations en secourisme et RCR.
              Facilement accessible en voiture, en bus ou à vélo.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                href={OSM_URL} target="_blank"
                variant="contained"
                startIcon={<DirectionsIcon />}
                sx={{
                  background: '#fff', color: '#c62828',
                  fontWeight: 700, px: 4, py: 1.4, borderRadius: '50px',
                  '&:hover': { background: 'rgba(255,255,255,0.9)', transform: 'translateY(-2px)' },
                }}
              >
                Obtenir l'itinéraire
              </Button>
              <Button
                href="tel:4503005005"
                variant="outlined"
                startIcon={<PhoneIcon />}
                sx={{
                  borderColor: 'rgba(255,255,255,0.6)', color: '#fff',
                  fontWeight: 700, px: 4, py: 1.4, borderRadius: '50px',
                  '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
                }}
              >
                450 300-5005
              </Button>
            </Stack>
          </Box>

          <Box sx={{ mt: 6, animation: `${bounceY} 2.2s ease infinite` }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 30, opacity: 0.55 }} />
          </Box>
        </Wrap>
      </HeroSection>

      {/* ══════════════ CARTE + INFOS ══════════════ */}
      <Section>
        <Wrap>
          <Grid container spacing={3} alignItems="stretch">

            {/* Carte OpenStreetMap */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{
                width: '100%',
                height: { xs: '300px', sm: '420px', md: '540px' },
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 16px 48px rgba(0,0,0,0.13)',
                border: '1px solid rgba(198,40,40,0.1)',
                position: 'relative',
              }}>
                <iframe
                  title="Carte - Académie Vita Secours"
                  width="100%" height="100%"
                  frameBorder="0" scrolling="no"
                  src={OSM_EMBED}
                  style={{ border: 'none', display: 'block', filter: 'contrast(108%)' }}
                  loading="lazy"
                />
                {/* Lien plein écran */}
                <Box sx={{
                  position: 'absolute', bottom: 12, right: 12,
                  background: 'rgba(255,255,255,0.96)',
                  borderRadius: '10px', px: 1.5, py: 0.75,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                }}>
                  <Button
                    href={OSM_URL} target="_blank" rel="noopener noreferrer"
                    size="small" endIcon={<OpenInNewIcon sx={{ fontSize: '0.9rem' }} />}
                    sx={{ color: '#c62828', fontWeight: 700, fontSize: '0.75rem', p: 0, textTransform: 'none', '&:hover': { background: 'none', textDecoration: 'underline' } }}
                  >
                    Plein écran
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Panneau d'infos */}
            <Grid size={{ xs: 12, md: 4 }}>
              <InfoCard elevation={0}>
                {/* En-tête rouge */}
                <Box sx={{
                  background: 'linear-gradient(135deg, #c62828, #8e0000)',
                  p: 3, color: '#fff',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <LocationOnIcon sx={{ fontSize: 22 }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        Académie Vita Secours
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        Saint-Jérôme, Québec
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{
                    background: 'rgba(255,255,255,0.15)',
                    borderRadius: '10px',
                    p: 1.5,
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.5 }}>
                      275 Des Laurentides
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.88 }}>
                      Saint-Jérôme, QC J7Z 7K8
                    </Typography>
                  </Box>
                </Box>

                {/* Corps des infos */}
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { icon: <AccessTimeIcon    sx={{ fontSize: 18, color: '#c62828' }} />, label: 'Horaires',        val: 'Lun–Ven : 8h – 17h' },
                    { icon: <LocalParkingIcon  sx={{ fontSize: 18, color: '#c62828' }} />, label: 'Stationnement',   val: 'Gratuit sur place' },
                    { icon: <AccessibilityIcon sx={{ fontSize: 18, color: '#c62828' }} />, label: 'Accessibilité',   val: 'Accès PMR complet' },
                    { icon: <WifiIcon          sx={{ fontSize: 18, color: '#c62828' }} />, label: 'Wi-Fi',           val: 'Gratuit & haut débit' },
                  ].map((row, i) => (
                    <Box key={i} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      p: 1.25, borderRadius: '10px',
                      border: '1px solid rgba(0,0,0,0.05)',
                      backgroundColor: '#fafafa',
                      transition: 'all 0.2s',
                      '&:hover': { backgroundColor: 'rgba(198,40,40,0.03)', borderColor: 'rgba(198,40,40,0.15)' },
                    }}>
                      <Box sx={{ flexShrink: 0 }}>{row.icon}</Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {row.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#333', fontWeight: 600, fontSize: '0.85rem' }}>
                          {row.val}
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                  <Divider sx={{ my: 0.5 }} />

                  {/* Contacts */}
                  <Stack spacing={1}>
                    <Button
                      href="tel:4503005005"
                      variant="contained" fullWidth startIcon={<PhoneIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #c62828, #b71c1c)',
                        fontWeight: 700, fontSize: '0.85rem', py: 1, borderRadius: '10px',
                        textTransform: 'none',
                        '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #9a1515)', transform: 'translateY(-1px)' },
                      }}
                    >
                      450 300-5005
                    </Button>
                    <Button
                      href="mailto:info@vitasecours.ca"
                      variant="outlined" fullWidth startIcon={<EmailIcon />}
                      sx={{
                        borderColor: '#c62828', borderWidth: 1.5,
                        color: '#c62828', fontWeight: 600, fontSize: '0.85rem',
                        py: 1, borderRadius: '10px', textTransform: 'none',
                        '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(198,40,40,0.05)' },
                      }}
                    >
                      info@vitasecours.ca
                    </Button>
                  </Stack>
                </Box>
              </InfoCard>
            </Grid>
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════ COMMENT NOUS REJOINDRE ══════════════ */}
      <Section alt>
        <Wrap>
          <SectionLabel>Accès</SectionLabel>
          <SectionTitle variant="h3">Comment nous rejoindre ?</SectionTitle>
          <Grid container spacing={3} justifyContent="center">
            {TRANSPORTS.map((t, i) => (
              <Grid key={i} size={{ xs: 12, sm: 4 }}>
                <TransportCard elevation={1} cardcolor={t.color}>
                  <Box sx={{
                    width: 58, height: 58,
                    borderRadius: '16px',
                    background: `${t.color}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: t.color,
                  }}>
                    {t.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1rem', mb: 0.25 }}>
                      {t.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: t.color, fontWeight: 600, fontSize: '0.8rem' }}>
                      {t.sub}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7, fontSize: '0.88rem' }}>
                    {t.desc}
                  </Typography>
                </TransportCard>
              </Grid>
            ))}
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════ SERVICES + RESTAURATION ══════════════ */}
      <Section>
        <Wrap>
          <Grid container spacing={5} alignItems="flex-start">

            {/* Services sur place */}
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionLabel>Sur place</SectionLabel>
              <SectionTitle variant="h3" sx={{ textAlign: { xs: 'center', md: 'left' }, '&::after': { left: { md: '22px' }, transform: { md: 'none' } } }}>
                Services disponibles
              </SectionTitle>
              <Stack spacing={1.5}>
                {SERVICES.map((s, i) => (
                  <ServiceRow key={i}>
                    <RoundIcon size={46}>{s.icon}</RoundIcon>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '0.92rem' }}>
                        {s.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#777', fontSize: '0.83rem', lineHeight: 1.5 }}>
                        {s.desc}
                      </Typography>
                    </Box>
                  </ServiceRow>
                ))}
              </Stack>
            </Grid>

            {/* Restauration à proximité */}
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionLabel>À pieds</SectionLabel>
              <SectionTitle variant="h3" sx={{ textAlign: { xs: 'center', md: 'left' }, '&::after': { left: { md: '22px' }, transform: { md: 'none' } } }}>
                Restauration à proximité
              </SectionTitle>
              <Paper elevation={0} sx={{ borderRadius: '18px', border: '1px solid rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                {RESTAURANTS.map((r, i) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2.5, py: 1.75,
                    borderBottom: i < RESTAURANTS.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                    transition: 'background 0.2s',
                    '&:hover': { backgroundColor: 'rgba(198,40,40,0.025)' },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircleIcon sx={{ color: '#c62828', fontSize: '1rem', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                        {r.name}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${r.distance} à pied`}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(198,40,40,0.08)',
                        color: '#c62828',
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        height: 24,
                      }}
                    />
                  </Box>
                ))}
                <Box sx={{ px: 2.5, py: 2, backgroundColor: '#fafafa', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                  <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                    Tous accessibles à pied depuis notre local. Cuisine disponible sur place si vous apportez votre repas.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════ GALERIE BÂTISSE ══════════════ */}
      <Section alt>
        <Wrap>
          <Grid container spacing={5} alignItems="center">
            {/* Texte */}
            <Grid size={{ xs: 12, md: 5 }}>
              <SectionLabel>Notre bâtisse</SectionLabel>
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 2.5, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
                Des locaux modernes et accueillants
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a4a4a', lineHeight: 1.9, fontSize: '1.05rem', mb: 3 }}>
                Notre local principal est situé au{' '}
                <strong style={{ color: '#c62828' }}>275 Des Laurentides à Saint-Jérôme</strong>,
                dans un bâtiment moderne facilement accessible par tous les moyens de transport.
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a4a4a', lineHeight: 1.9, fontSize: '1.05rem', mb: 4 }}>
                Nos salles de formation sont équipées des dernières technologies pédagogiques
                pour un apprentissage optimal du secourisme.
              </Typography>
              <Button
                href={OSM_URL} target="_blank"
                variant="contained"
                startIcon={<DirectionsIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #c62828, #8e0000)',
                  fontWeight: 700, px: 4, py: 1.4, borderRadius: '50px',
                  '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #7f0000)', transform: 'translateY(-2px)' },
                }}
              >
                Voir l'itinéraire
              </Button>
            </Grid>

            {/* Carrousel */}
            <Grid size={{ xs: 12, md: 7 }}>
              <CarouselContainer>
                <Box sx={{ display: 'flex', transition: 'transform 0.5s ease-in-out', transform: `translateX(-${imgIdx * 100}%)` }}>
                  {images.map((img, i) => (
                    <Box key={i} sx={{ width: '100%', flexShrink: 0, position: 'relative' }}>
                      <Box
                        component="img"
                        src={img}
                        alt={`Vita Secours bâtisse ${i + 1}`}
                        sx={{ width: '100%', height: { xs: '260px', sm: '380px' }, objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                  ))}
                </Box>
                <CarouselBtn className="left" onClick={() => setImgIdx(p => (p - 1 + images.length) % images.length)}>
                  <ArrowBackIosNewIcon sx={{ fontSize: '0.85rem' }} />
                </CarouselBtn>
                <CarouselBtn className="right" onClick={() => setImgIdx(p => (p + 1) % images.length)}>
                  <ArrowForwardIosIcon sx={{ fontSize: '0.85rem' }} />
                </CarouselBtn>
                {/* Dots */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, py: 1.5, backgroundColor: 'rgba(0,0,0,0.03)' }}>
                  {images.map((_, i) => (
                    <Box key={i} onClick={() => setImgIdx(i)} sx={{
                      width: i === imgIdx ? 22 : 8, height: 8,
                      borderRadius: '4px',
                      backgroundColor: i === imgIdx ? '#c62828' : 'rgba(198,40,40,0.25)',
                      cursor: 'pointer', transition: 'all 0.3s',
                    }} />
                  ))}
                </Box>
              </CarouselContainer>
            </Grid>
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════ CTA ══════════════ */}
      <Section>
        <Wrap>
          <Box sx={{
            textAlign: 'center',
            background: 'linear-gradient(135deg, #fff 0%, #fef7f7 100%)',
            borderRadius: '28px',
            border: '1px solid rgba(198,40,40,0.1)',
            boxShadow: '0 16px 52px rgba(198,40,40,0.08)',
            p: { xs: 5, md: 8 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""', position: 'absolute',
              top: 0, left: 0, right: 0, height: '4px',
              background: 'linear-gradient(90deg, #c62828 0%, #ff6b6b 50%, #c62828 100%)',
            },
          }}>
            <Typography variant="h3" sx={{ fontWeight: 900, color: '#c62828', mb: 2, fontSize: { xs: '1.8rem', md: '2.3rem' } }}>
              Besoin d'indications ?
            </Typography>
            <Typography sx={{ mb: 4.5, color: '#666', maxWidth: '540px', mx: 'auto', fontSize: '1.05rem', lineHeight: 1.75 }}>
              Notre équipe est disponible pour vous guider jusqu'à notre local de Saint-Jérôme.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} justifyContent="center">
              <Button
                href={OSM_URL} target="_blank"
                variant="contained" size="large" startIcon={<DirectionsIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #c62828, #8e0000)',
                  color: '#fff', px: 5, py: 1.7,
                  fontWeight: 800, fontSize: '1rem', borderRadius: '50px',
                  boxShadow: '0 10px 28px rgba(198,40,40,0.28)',
                  '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #7f0000)', transform: 'translateY(-3px)' },
                }}
              >
                Itinéraire OpenStreetMap
              </Button>
              <Button
                href="tel:4503005005"
                variant="outlined" size="large" startIcon={<PhoneIcon />}
                sx={{
                  borderColor: '#c62828', borderWidth: 2,
                  color: '#c62828', px: 5, py: 1.7,
                  fontWeight: 800, fontSize: '1rem', borderRadius: '50px',
                  '&:hover': { borderWidth: 2, borderColor: '#b71c1c', background: 'rgba(198,40,40,0.05)', transform: 'translateY(-3px)' },
                }}
              >
                Appelez-nous
              </Button>
            </Stack>
          </Box>
        </Wrap>
      </Section>
    </>
  );
};

export default Emplacement;
